import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, HelpCircle, Wrench, Truck, Settings, DollarSign, Palette, FileDown, FileText } from 'lucide-react';
import { useToastNotifications } from '@/components/ui/toast-notification';
import { jsPDF } from 'jspdf';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: 'servis' | 'dodavky' | 'technické' | 'obchod' | 'barvy';
  language: string;
  keywords: string[];
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const FAQ_CATEGORIES = [
  { value: 'servis', label: 'Servis a údržba', icon: Wrench },
  { value: 'dodavky', label: 'Dodávky a logistika', icon: Truck },
  { value: 'technické', label: 'Technické specifikace', icon: Settings },
  { value: 'obchod', label: 'Obchodní podmínky', icon: DollarSign },
  { value: 'barvy', label: 'Barvy a design', icon: Palette }
];

export function FAQEditTab() {
  const { addNotification } = useToastNotifications();
  const queryClient = useQueryClient();
  const [editingFAQ, setEditingFAQ] = useState<FAQItem | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [exportState, setExportState] = useState<'pdf' | 'txt' | null>(null);
  const assetCacheRef = useRef<{
    regularFont?: string;
    semiBoldFont?: string;
    logo?: string;
  }>({});
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'servis',
    language: 'cs',
    keywords: '',
    is_active: true,
    sort_order: 0
  });

  // Fetch FAQ items
  const { data: faqData, isLoading } = useQuery({
    queryKey: ['admin-faq-items'],
    queryFn: async () => {
      const response = await fetch('/api/admin/faq');
      if (!response.ok) throw new Error('Failed to fetch FAQ items');
      return response.json();
    }
  });
  const faqItems: FAQItem[] = faqData?.data || [];

  // Create FAQ mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch('/api/admin/faq', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          keywords: data.keywords.split(',').map((k: string) => k.trim()).filter((k: string) => k.length > 0)
        })
      });
      if (!response.ok) throw new Error('Failed to create FAQ item');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-items'] });
      setIsDialogOpen(false);
      resetForm();
      addNotification('success', 'FAQ položka byla úspěšně vytvořena');
    },
    onError: (error) => {
      addNotification('error', 'Chyba při vytváření FAQ: ' + error.message);
    }
  });

  // Update FAQ mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await fetch(`/api/admin/faq/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          keywords: data.keywords.split(',').map((k: string) => k.trim()).filter((k: string) => k.length > 0)
        })
      });
      if (!response.ok) throw new Error('Failed to update FAQ item');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-items'] });
      setIsDialogOpen(false);
      setEditingFAQ(null);
      resetForm();
      addNotification('success', 'FAQ položka byla úspěšně aktualizována');
    },
    onError: (error) => {
      addNotification('error', 'Chyba při aktualizaci FAQ: ' + error.message);
    }
  });

  // Delete FAQ mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/admin/faq/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete FAQ item');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-items'] });
      addNotification('success', 'FAQ položka byla úspěšně smazána');
    },
    onError: (error) => {
      addNotification('error', 'Chyba při mazání FAQ: ' + error.message);
    }
  });

  const resetForm = () => {
    setFormData({
      question: '',
      answer: '',
      category: 'servis',
      language: 'cs',
      keywords: '',
      is_active: true,
      sort_order: 0
    });
  };

  const handleEdit = (faq: FAQItem) => {
    setEditingFAQ(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      language: faq.language,
      keywords: faq.keywords.join(', '),
      is_active: faq.is_active,
      sort_order: faq.sort_order
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingFAQ) {
      updateMutation.mutate({ id: editingFAQ.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: number) => {
    if (confirm('Opravdu chcete smazat tuto FAQ položku?')) {
      deleteMutation.mutate(id);
    }
  };

  const openNewDialog = () => {
    setEditingFAQ(null);
    resetForm();
    setIsDialogOpen(true);
  };

  const getCategoryIcon = (category: string) => {
    const categoryData = FAQ_CATEGORIES.find(c => c.value === category);
    const Icon = categoryData?.icon || HelpCircle;
    return <Icon className="h-4 w-4" />;
  };

  const getCategoryLabel = (category: string) => {
    return FAQ_CATEGORIES.find(c => c.value === category)?.label || category;
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';

    for (let i = 0; i < bytes.byteLength; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return window.btoa(binary);
  };

  const fetchFontBase64 = async (path: string) => {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error('Nepodařilo se načíst font pro export');
    }
    const buffer = await response.arrayBuffer();
    return arrayBufferToBase64(buffer);
  };

  const fetchLogoDataUrl = async (path: string) => {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error('Nepodařilo se načíst logo pro export');
    }
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Nepodařilo se načíst logo pro export'));
      reader.readAsDataURL(blob);
    });
  };

  const handleExport = async (format: 'pdf' | 'txt') => {
    if (!faqItems || faqItems.length === 0) {
      addNotification('warning', 'K exportu nejsou dostupné žádné FAQ položky');
      return;
    }

    if (typeof window === 'undefined') {
      addNotification('error', 'Export je dostupný pouze v prohlížeči');
      return;
    }

    setExportState(format);

    const generatedAt = new Date();
    const timestamp = generatedAt.toISOString().replace(/[:.]/g, '-');
    const sortedFaqs = [...faqItems].sort((a, b) => {
      if (a.language !== b.language) {
        return a.language.localeCompare(b.language);
      }
      if (a.sort_order !== b.sort_order) {
        return a.sort_order - b.sort_order;
      }
      return a.id - b.id;
    });

    try {
      if (format === 'txt') {
        const header = [
          'BLAZE HARMONY - FAQ Export',
          `Vygenerováno: ${generatedAt.toLocaleString('cs-CZ')}`,
          `Celkem položek: ${sortedFaqs.length}`,
          ''
        ].join('\n');

        const textSections = sortedFaqs.map((faq, index) => {
          const lines = [
            `${index + 1}. ${faq.question}`,
            '',
            faq.answer,
            '',
            '---',
            ''
          ];

          return lines.join('\n');
        });

        const textContent = [header, ...textSections].join('\n');
        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `faq_export_${timestamp}.txt`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        addNotification('success', 'FAQ export byl uložen jako TXT soubor');
      } else {
        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        const cache = assetCacheRef.current;

        const [regularFont, semiBoldFont, logoDataUrl] = await Promise.all([
          cache.regularFont ? Promise.resolve(cache.regularFont) : fetchFontBase64('/fonts/poppins-regular.ttf'),
          cache.semiBoldFont ? Promise.resolve(cache.semiBoldFont) : fetchFontBase64('/fonts/poppins-semibold.ttf'),
          cache.logo ? Promise.resolve(cache.logo) : fetchLogoDataUrl('/images/bh-white-orange.png')
        ]);

        cache.regularFont = regularFont;
        cache.semiBoldFont = semiBoldFont;
        cache.logo = logoDataUrl;

        doc.addFileToVFS('Poppins-Regular.ttf', regularFont);
        doc.addFont('Poppins-Regular.ttf', 'Poppins', 'normal');
        doc.addFileToVFS('Poppins-SemiBold.ttf', semiBoldFont);
        doc.addFont('Poppins-SemiBold.ttf', 'Poppins', 'bold');
        doc.setFont('Poppins', 'normal');

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 48;
        const contentWidth = pageWidth - margin * 2;
        const cardPadding = 24;
        const cardSpacing = 24;
        const cardContentWidth = contentWidth - cardPadding * 2;
        const brandColor = { r: 217, g: 119, b: 6 };
        const headerColor = { r: 51, g: 51, b: 51 };
        const backgroundColor = { r: 248, g: 247, b: 245 };
        const cardBackground = { r: 255, g: 248, b: 241 };

        const drawPageBackground = () => {
          doc.setFillColor(backgroundColor.r, backgroundColor.g, backgroundColor.b);
          doc.rect(0, 0, pageWidth, pageHeight, 'F');
        };

        const renderHeader = (firstPage: boolean) => {
          const headerHeight = firstPage ? 132 : 72;
          doc.setFillColor(headerColor.r, headerColor.g, headerColor.b);
          doc.rect(0, 0, pageWidth, headerHeight, 'F');

          const logoHeight = firstPage ? 46 : 36;
          const logoWidth = logoHeight * 2.4;
          const logoX = pageWidth - margin - logoWidth;
          const logoY = firstPage ? 34 : 22;
          if (logoDataUrl) {
            doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
          }

          doc.setFont('Poppins', 'bold');
          doc.setFontSize(firstPage ? 24 : 20);
          doc.setTextColor(255, 255, 255);
          doc.text('FAQ Export', margin, firstPage ? 70 : 60);

          doc.setFont('Poppins', 'normal');
          doc.setFontSize(11);
          doc.setTextColor(255, 248, 240);
          doc.text(`Datum exportu: ${generatedAt.toLocaleDateString('cs-CZ')}`, margin, firstPage ? 92 : 82);
          doc.text(`Celkem položek: ${sortedFaqs.length}`, margin, firstPage ? 110 : 98);

          doc.setFont('Poppins', 'normal');
          doc.setFontSize(11);
          doc.setTextColor(55, 65, 81);

          return headerHeight;
        };

        drawPageBackground();
        let y = renderHeader(true) + 32;

        const ensureSpace = (requiredHeight: number) => {
          if (y + requiredHeight > pageHeight - margin) {
            doc.addPage();
            drawPageBackground();
            y = renderHeader(false) + 24;
          }
        };

        sortedFaqs.forEach((faq, index) => {
          const questionText = `${index + 1}. ${faq.question}`;
          doc.setFont('Poppins', 'bold');
          doc.setFontSize(13);
          doc.setTextColor(brandColor.r, brandColor.g, brandColor.b);
          const questionLines = doc.splitTextToSize(questionText, cardContentWidth);

          doc.setFont('Poppins', 'normal');
          doc.setFontSize(11);
          doc.setTextColor(31, 41, 55);
          const answerLines = doc.splitTextToSize(faq.answer, cardContentWidth);

          const questionLineHeight = 18;
          const answerLineHeight = 16;
          const blockSpacing = 14;
          const topOffset = 16;
          const bottomPadding = Math.max(cardPadding / 2, 18);
          const cardHeight =
            cardPadding +
            topOffset +
            questionLines.length * questionLineHeight +
            blockSpacing +
            answerLines.length * answerLineHeight +
            bottomPadding;

          ensureSpace(cardHeight + cardSpacing);

          doc.setDrawColor(brandColor.r, brandColor.g, brandColor.b);
          doc.setLineWidth(1.2);
          doc.setFillColor(cardBackground.r, cardBackground.g, cardBackground.b);
          doc.roundedRect(margin, y, contentWidth, cardHeight, 14, 14, 'FD');

          let textY = y + cardPadding + topOffset;

          doc.setFont('Poppins', 'bold');
          doc.setFontSize(13);
          doc.setTextColor(brandColor.r, brandColor.g, brandColor.b);
          questionLines.forEach((line) => {
            doc.text(line, margin + cardPadding, textY);
            textY += questionLineHeight;
          });
          textY += blockSpacing;

          doc.setFont('Poppins', 'normal');
          doc.setFontSize(11);
          doc.setTextColor(31, 41, 55);
          answerLines.forEach((line) => {
            doc.text(line, margin + cardPadding, textY);
            textY += answerLineHeight;
          });

          y += cardHeight + cardSpacing;
        });

        doc.setFont('Poppins', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(107, 114, 128);
        doc.text('Generováno interním systémem BLAZE HARMONY', margin, pageHeight - margin + 24);

        doc.save(`faq_export_${timestamp}.pdf`);
        addNotification('success', 'FAQ export byl uložen jako PDF soubor');
      }
    } catch (error) {
      console.error('FAQ export error:', error);
      addNotification('error', 'Export FAQ se nezdařil');
    } finally {
      setExportState(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                Správa FAQ
              </CardTitle>
              <CardDescription>
                Spravujte často kladené otázky a odpovědi
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                variant="outline"
                type="button"
                onClick={() => void handleExport('pdf')}
                disabled={exportState !== null}
                className="flex items-center gap-2"
              >
                <FileDown className="h-4 w-4" />
                {exportState === 'pdf' ? 'Exportuji…' : 'Export PDF'}
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => void handleExport('txt')}
                disabled={exportState !== null}
                className="flex items-center gap-2"
              >
                <FileText className="h-4 w-4" />
                {exportState === 'txt' ? 'Exportuji…' : 'Export TXT'}
              </Button>
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openNewDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nová FAQ
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingFAQ ? 'Upravit FAQ položku' : 'Nová FAQ položka'}
                    </DialogTitle>
                    <DialogDescription>
                      {editingFAQ ? 'Upravte existující FAQ položku' : 'Přidejte novou často kladenou otázku'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Jazyk</Label>
                        <RadioGroup value={formData.language} onValueChange={(value: string) => setFormData({ ...formData, language: value })}>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="cs" id="cs" />
                            <Label htmlFor="cs">🇨🇿 Čeština</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="en" id="en" />
                            <Label htmlFor="en">🇺🇸 Angličtina</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="category">Kategorie</Label>
                        <select
                          id="category"
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {FAQ_CATEGORIES.map(category => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="question">Otázka *</Label>
                      <Textarea
                        id="question"
                        value={formData.question}
                        onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                        placeholder="Zadejte otázku..."
                        rows={3}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="answer">Odpověď *</Label>
                      <Textarea
                        id="answer"
                        value={formData.answer}
                        onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                        placeholder="Zadejte odpověď..."
                        rows={8}
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Pro nový řádek použijte \\n. HTML tagy nejsou podporovány.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="keywords">Klíčová slova</Label>
                      <Input
                        id="keywords"
                        value={formData.keywords}
                        onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                        placeholder="servis, školení, podpora, kamnář (oddělte čárkami)"
                      />
                      <p className="text-xs text-muted-foreground">
                        Klíčová slova pro vyhledávání, oddělte čárkami
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="sort_order">Pořadí</Label>
                        <Input
                          id="sort_order"
                          type="number"
                          value={formData.sort_order}
                          onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                          placeholder="0"
                        />
                      </div>
                      <div className="flex items-center space-x-2 pt-6">
                        <Checkbox
                          id="is_active"
                          checked={formData.is_active}
                          onCheckedChange={(checked: boolean) => setFormData({ ...formData, is_active: checked })}
                        />
                        <Label htmlFor="is_active">Aktivní</Label>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                        Zrušit
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={createMutation.isPending || updateMutation.isPending}
                      >
                        {editingFAQ ? 'Uložit změny' : 'Vytvořit FAQ'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p>Načítám FAQ...</p>
            </div>
          ) : faqItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <HelpCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Žádné FAQ položky</p>
              <p className="text-sm">Přidejte první často kladenou otázku</p>
            </div>
          ) : (
            <div className="space-y-4">
              {faqItems
                .sort((a: FAQItem, b: FAQItem) => a.sort_order - b.sort_order)
                .map((faq: FAQItem) => (
                <Card key={faq.id} className="border-l-4 border-l-primary/20">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-base mb-2 break-words">
                          {faq.question}
                        </div>
                        <div className="text-sm text-muted-foreground mb-3 break-words whitespace-pre-line">
                          {faq.answer.length > 200 
                            ? `${faq.answer.substring(0, 200)}...`
                            : faq.answer
                          }
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-1">
                            {getCategoryIcon(faq.category)}
                            <Badge variant="secondary" className="text-xs">
                              {getCategoryLabel(faq.category)}
                            </Badge>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {faq.language.toUpperCase()}
                          </Badge>
                          <Badge variant={faq.is_active ? "default" : "secondary"} className="text-xs">
                            {faq.is_active ? 'Aktivní' : 'Neaktivní'}
                          </Badge>
                          {faq.keywords.length > 0 && (
                            <div className="flex items-center gap-1">
                              {faq.keywords.slice(0, 3).map((keyword, index) => (
                                <Badge key={index} variant="outline" className="text-xs opacity-60">
                                  {keyword}
                                </Badge>
                              ))}
                              {faq.keywords.length > 3 && (
                                <Badge variant="outline" className="text-xs opacity-60">
                                  +{faq.keywords.length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(faq)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(faq.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <HelpCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Tip:</strong> Použijte klíčová slova pro lepší vyhledávání. FAQ položky jsou automaticky řazeny podle pořadí.
          Neaktivní položky se nezobrazují uživatelům.
        </AlertDescription>
      </Alert>
    </div>
  );
}
